import React, { useEffect, useState } from "react";
import { type ExternalToast, toast as sonnerToast, Toaster as SonnerToaster } from "sonner";
import { AlertCircle, CheckCircle2, Info, X } from "lucide-react";
import { cn } from "./utils";
import { Button } from "./button";
import Spinner from "./spinner";
// Type definitions for our custom toast
interface ToastProps {
    id: string | number;
    title: string;
    description?: string;
    type?: "success" | "error" | "warning" | "info" | "loading" | "default";
}

// Custom Toast Component with full Tailwind styling control
function Toast(props: ToastProps) {
    const { title, description, type = "default", id } = props;

    // Icon and color mapping based on toast type
    const styles = {
        success: {
            icon: <CheckCircle2 className="w-5 h-5 text-green-600" />,
            bg: "bg-white dark:bg-slate-950",
            border: "border-green-200 dark:border-green-900",
            titleColor: "text-gray-900 dark:text-slate-50",
        },
        error: {
            icon: <X className="w-5 h-5 text-red-600" />,
            bg: "bg-white dark:bg-slate-950",
            border: "border-red-200 dark:border-red-900",
            titleColor: "text-gray-900 dark:text-slate-50",
        },
        warning: {
            icon: <AlertCircle className="w-5 h-5 text-amber-600" />,
            bg: "bg-white dark:bg-slate-950",
            border: "border-amber-200 dark:border-amber-900",
            titleColor: "text-gray-900 dark:text-slate-50",
        },
        info: {
            icon: <Info className="w-5 h-5 text-blue-600" />,
            bg: "bg-white dark:bg-slate-950",
            border: "border-blue-200 dark:border-blue-900",
            titleColor: "text-gray-900 dark:text-slate-50",
        },
        loading: {
            icon: <Spinner inline className="w-5 h-5" />,
            bg: "bg-white dark:bg-slate-950",
            border: "border-slate-200 dark:border-slate-800",
            titleColor: "text-gray-900 dark:text-slate-50",
        },
        default: {
            icon: null,
            bg: "bg-white dark:bg-slate-950",
            border: "border-slate-200 dark:border-slate-800",
            titleColor: "text-gray-900 dark:text-slate-50",
        },
    };

    const style = styles[type];

    return (
        <div
            className={cn(
                "flex items-start gap-3 shadow-lg p-4 border rounded-lg ring-1 ring-black/5 w-full font-inclusive",
                style.bg,
                style.border
            )}
        >
            {style.icon && <div className="flex-shrink-0 mt-0.5">{style.icon}</div>}
            <div className="flex-1 min-w-0">
                <p className={cn("font-normal text-sm", style.titleColor)}>{title}</p>
                {description && (
                    <p className="mt-1 font-normal text-gray-500 dark:text-slate-400 text-sm">{description}</p>
                )}
            </div>
            {type !== "loading" && (
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => sonnerToast.dismiss(id)}
                    className={cn("w-6 h-6")}
                    aria-label="Dismiss"
                >
                    <X className="w-4 h-4 text-slate-800 dark:text-slate-400" />
                </Button>
            )}
        </div>
    );
}

const showWarningToast = (message: string, options?: { description?: string } & ExternalToast) => {
    const { description, ...rest } = options || {};
    return sonnerToast.custom((id) => <Toast id={id} title={message} description={description} type="warning" />, rest);
};

// Custom toast functions that wrap sonner's toast.custom
const customToast = {
    success: (message: string, options?: { description?: string } & ExternalToast) => {
        const { description, ...rest } = options || {};
        return sonnerToast.custom(
            (id) => <Toast id={id} title={message} description={description} type="success" />,
            rest
        );
    },
    error: (message: string, options?: { description?: string } & ExternalToast) => {
        const { description, ...rest } = options || {};
        return sonnerToast.custom(
            (id) => <Toast id={id} title={message} description={description} type="error" />,
            rest
        );
    },
    warning: showWarningToast,
    warn: showWarningToast,
    info: (message: string, options?: { description?: string } & ExternalToast) => {
        const { description, ...rest } = options || {};
        return sonnerToast.custom(
            (id) => <Toast id={id} title={message} description={description} type="info" />,
            rest
        );
    },
    loading: (message: string, options?: { description?: string } & ExternalToast) => {
        const { description, ...rest } = options || {};
        return sonnerToast.custom((id) => <Toast id={id} title={message} description={description} type="loading" />, {
            duration: Number.POSITIVE_INFINITY,
            ...rest,
        });
    },
    // Default toast for backward compatibility
    custom: sonnerToast.custom,
    // Expose dismiss for manual dismissal
    dismiss: sonnerToast.dismiss,
    // Expose promise for loading states
    promise: sonnerToast.promise,
};

/** Matches Tailwind `md` - corner toasts from tablet up; phones stay top-center. */
const MD_TOAST_MEDIA_QUERY = "(min-width: 768px)";

function useMediaQuery(query: string): boolean {
    const [matches, setMatches] = useState(() =>
        typeof window !== "undefined" ? window.matchMedia(query).matches : false
    );

    useEffect(() => {
        const media = window.matchMedia(query);
        const listener = () => setMatches(media.matches);
        listener();
        media.addEventListener("change", listener);
        return () => media.removeEventListener("change", listener);
    }, [query]);

    return matches;
}

// Toaster component - headless version
const Toaster = ({
    position,
    offset,
    mobileOffset,
    toastOptions,
    ...props
}: React.ComponentProps<typeof SonnerToaster>) => {
    const isMdUp = useMediaQuery(MD_TOAST_MEDIA_QUERY);
    // Mobile: top-center (away from thumbs / bottom chrome). md+: bottom-right.
    const resolvedPosition = position ?? (isMdUp ? "bottom-right" : "top-center");

    return (
        <SonnerToaster
            {...props}
            position={resolvedPosition}
            // Default Sonner offset is 32px; 16px sits tighter to the viewport edge.
            offset={offset ?? 16}
            mobileOffset={mobileOffset ?? 16}
            // Sonner 1.7 lacks toast-level `right: 0` (fixed in 2.x), so custom toasts
            // shrink-to-content and left-align inside the toaster on desktop.
            toastOptions={{
                ...toastOptions,
                style: {
                    ...(isMdUp ? { right: 0, width: "var(--width)" } : null),
                    ...toastOptions?.style,
                },
            }}
            // Pass empty className to avoid default styles
            className=""
        />
    );
};

export { customToast as toast, Toaster };
